import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const inputCls =
  "rounded-none border border-foreground/20 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-sm font-light";

export default function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/content/testimonials")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load testimonials"));
  }, []);

  const set = (i, k, v) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setItems((xs) => [...xs, { quote: "", name: "", origin: "", journey: "" }]);
  const remove = (i) => setItems((xs) => xs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/testimonials", items.filter((x) => x.quote && x.name));
      setItems(r.data);
      toast.success("Testimonials saved — live on the website now.");
    } catch {
      toast.error("Could not save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="testimonials-tab">
      <div className="flex items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">
          Testimonials <span className="text-foreground/40 text-2xl">({items.length})</span>
        </h2>
        <div className="flex gap-3">
          <button onClick={add} data-testid="testimonial-add" className="btn-sharp border border-foreground/30 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em]">
            + Add
          </button>
          <Button onClick={save} disabled={saving} data-testid="testimonials-save" className="btn-sharp rounded-none border border-primary bg-primary text-primary-foreground hover:bg-primary px-6 py-2.5 text-[10px] uppercase tracking-[0.2em]">
            {saving ? "Saving…" : "Save all"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-foreground/55 mb-6">
        The first testimonial is shown large as the featured quote on the home page.
      </p>

      <div className="space-y-6">
        {items.map((t, i) => (
          <div key={i} className="border border-foreground/15 p-5" data-testid={`testimonial-item-${i}`}>
            <div className="flex justify-between items-start gap-3 mb-3">
              <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/50">
                {i === 0 ? "Featured quote" : `Quote ${i + 1}`}
              </span>
              <button onClick={() => remove(i)} data-testid={`testimonial-remove-${i}`} className="text-foreground/40 hover:text-destructive text-lg leading-none">×</button>
            </div>
            <Textarea value={t.quote} onChange={(e) => set(i, "quote", e.target.value)} placeholder="The quote" rows={3} className={inputCls} data-testid={`testimonial-quote-${i}`} />
            <div className="grid md:grid-cols-3 gap-3 mt-3">
              <Input value={t.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Name" className={inputCls} />
              <Input value={t.origin} onChange={(e) => set(i, "origin", e.target.value)} placeholder="City, Country" className={inputCls} />
              <Input value={t.journey} onChange={(e) => set(i, "journey", e.target.value)} placeholder="Journey taken" className={inputCls} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
